import React, { useState, useEffect, useCallback } from 'react';
import { Copy, Download, Upload, RotateCcw, Maximize2, Minimize2, BarChart3, Code, Zap, CheckCircle2 } from 'lucide-react';

interface ProcessingStats {
  charactersProcessed: number;
  entitiesFound: number;
  processingTime: number;
  operationsCount: number;
}

interface ProcessingResult {
  result: string;
  stats: Omit<ProcessingStats, 'operationsCount'>;
}

type EscapeMode = 'escape' | 'unescape';
type PhpQuoteStyle = 'single' | 'double' | 'addslashes' | 'htmlspecialchars' | 'json' | 'url';

const PhpEscapeUnescapeTool: React.FC = () => {
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [mode, setMode] = useState<EscapeMode>('escape');
  const [quoteStyle, setQuoteStyle] = useState<PhpQuoteStyle>('single');
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  const [stats, setStats] = useState<ProcessingStats>({
    charactersProcessed: 0,
    entitiesFound: 0,
    processingTime: 0,
    operationsCount: 0
  });
  const [notification, setNotification] = useState<string>('');

  const escapePHP = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    switch (quoteStyle) {
      case 'single':
        // Single quote escaping (only escape single quotes and backslashes)
        const singleQuoteMatches = result.match(/'/g);
        if (singleQuoteMatches) {
          entitiesCount += singleQuoteMatches.length;
          result = result.replace(/'/g, "\\'");
        }
        
        const backslashMatches = result.match(/\\/g);
        if (backslashMatches) {
          entitiesCount += backslashMatches.length;
          result = result.replace(/\\/g, '\\\\');
        }
        break;

      case 'double':
        // Double quote escaping (escape quotes, backslashes, and special chars)
        const doubleQuoteMatches = result.match(/"/g);
        if (doubleQuoteMatches) {
          entitiesCount += doubleQuoteMatches.length;
          result = result.replace(/"/g, '\\"');
        }

        const backslashDoubleMatches = result.match(/\\/g);
        if (backslashDoubleMatches) {
          entitiesCount += backslashDoubleMatches.length;
          result = result.replace(/\\/g, '\\\\');
        }

        // Escape special characters in double quotes
        const specialChars = [
          { char: '\n', escaped: '\\n' },
          { char: '\r', escaped: '\\r' },
          { char: '\t', escaped: '\\t' },
          { char: '\v', escaped: '\\v' },
          { char: '\f', escaped: '\\f' },
          { char: '\b', escaped: '\\b' },
          { char: '\0', escaped: '\\0' },
          { char: '$', escaped: '\\$' }
        ];

        specialChars.forEach(({ char, escaped }) => {
          const matches = result.match(new RegExp(char === '$' ? '\\$' : char.replace(/[\n\r\t\v\f\b\0]/g, (m) => '\\' + m), 'g'));
          if (matches) {
            entitiesCount += matches.length;
            result = result.replace(new RegExp(char === '$' ? '\\$' : char.replace(/[\n\r\t\v\f\b\0]/g, (m) => '\\' + m), 'g'), escaped);
          }
        });
        break;

      case 'addslashes':
        // PHP addslashes() function behavior
        const addslashesChars = [
          { char: '\\', escaped: '\\\\' },
          { char: "'", escaped: "\\'" },
          { char: '"', escaped: '\\"' },
          { char: '\0', escaped: '\\0' }
        ];

        addslashesChars.forEach(({ char, escaped }) => {
          const regex = new RegExp(char === '\\' ? '\\\\' : char === '\0' ? '\0' : char.replace(/['"]/g, '\\$&'), 'g');
          const matches = result.match(regex);
          if (matches) {
            entitiesCount += matches.length;
            result = result.replace(regex, escaped);
          }
        });
        break;

      case 'htmlspecialchars':
        // PHP htmlspecialchars() function behavior
        const htmlChars = [
          { char: '&', escaped: '&amp;' },
          { char: '"', escaped: '&quot;' },
          { char: "'", escaped: '&#039;' },
          { char: '<', escaped: '&lt;' },
          { char: '>', escaped: '&gt;' }
        ];

        htmlChars.forEach(({ char, escaped }) => {
          const matches = result.match(new RegExp(char.replace(/[&"'<>]/g, '\\$&'), 'g'));
          if (matches) {
            entitiesCount += matches.length;
            result = result.replace(new RegExp(char.replace(/[&"'<>]/g, '\\$&'), 'g'), escaped);
          }
        });
        break;

      case 'json':
        // JSON encoding (similar to json_encode)
        try {
          result = JSON.stringify(text);
          entitiesCount = (result.match(/\\./g) || []).length;
          // Remove outer quotes added by JSON.stringify
          result = result.slice(1, -1);
        } catch (e) {
          result = text;
        }
        break;

      case 'url':
        // URL encoding (similar to urlencode)
        result = encodeURIComponent(text);
        entitiesCount = (result.match(/%/g) || []).length;
        break;
    }

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, [quoteStyle]);

  const unescapePHP = useCallback((text: string): ProcessingResult => {
    const startTime = performance.now();
    let result = text;
    let entitiesCount = 0;

    // Try to detect and unescape various PHP escape formats

    // Unescape HTML entities (htmlspecialchars_decode)
    const htmlEntities = [
      { escaped: '&amp;', char: '&' },
      { escaped: '&quot;', char: '"' },
      { escaped: '&#039;', char: "'" },
      { escaped: '&lt;', char: '<' },
      { escaped: '&gt;', char: '>' },
      { escaped: '&apos;', char: "'" }
    ];

    htmlEntities.forEach(({ escaped, char }) => {
      const matches = result.match(new RegExp(escaped.replace(/[&;]/g, '\\$&'), 'g'));
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(new RegExp(escaped.replace(/[&;]/g, '\\$&'), 'g'), char);
      }
    });

    // Unescape URL encoding
    const urlMatches = result.match(/%[0-9a-fA-F]{2}/g);
    if (urlMatches) {
      try {
        const decoded = decodeURIComponent(result);
        if (decoded !== result) {
          entitiesCount += urlMatches.length;
          result = decoded;
        }
      } catch (e) {
        // Invalid URL encoding, continue with other unescaping
      }
    }

    // Unescape PHP escape sequences
    const phpEscapes = [
      { escaped: '\\n', char: '\n' },
      { escaped: '\\r', char: '\r' },
      { escaped: '\\t', char: '\t' },
      { escaped: '\\v', char: '\v' },
      { escaped: '\\f', char: '\f' },
      { escaped: '\\b', char: '\b' },
      { escaped: '\\0', char: '\0' },
      { escaped: '\\"', char: '"' },
      { escaped: "\\'", char: "'" },
      { escaped: '\\$', char: '$' }
    ];

    phpEscapes.forEach(({ escaped, char }) => {
      const regex = new RegExp(escaped.replace(/\\/g, '\\\\'), 'g');
      const matches = result.match(regex);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(regex, char);
      }
    });

    // Unescape JSON escape sequences
    const jsonEscapes = [
      { escaped: '\\/', char: '/' },
      { escaped: '\\\\', char: '\\' }
    ];

    jsonEscapes.forEach(({ escaped, char }) => {
      const regex = new RegExp(escaped.replace(/\\/g, '\\\\'), 'g');
      const matches = result.match(regex);
      if (matches) {
        entitiesCount += matches.length;
        result = result.replace(regex, char);
      }
    });

    // Unescape Unicode sequences (\uXXXX)
    result = result.replace(/\\u([0-9a-fA-F]{4})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 0xFFFF) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape hexadecimal sequences (\xXX)
    result = result.replace(/\\x([0-9a-fA-F]{2})/g, (match, hex) => {
      const code = parseInt(hex, 16);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    // Unescape octal sequences (\XXX)
    result = result.replace(/\\([0-7]{1,3})/g, (match, oct) => {
      if (oct === '0') return match; // Keep \0 as is
      const code = parseInt(oct, 8);
      if (code >= 0 && code <= 255) {
        entitiesCount++;
        return String.fromCharCode(code);
      }
      return match;
    });

    const endTime = performance.now();
    return {
      result,
      stats: {
        charactersProcessed: text.length,
        entitiesFound: entitiesCount,
        processingTime: endTime - startTime
      }
    };
  }, []);

  const processText = useCallback((): void => {
    if (!inputText.trim()) {
      setOutputText('');
      return;
    }

    const processor = mode === 'escape' ? escapePHP : unescapePHP;
    const { result, stats: processingStats } = processor(inputText);
    
    setOutputText(result);
    setStats(prev => ({
      ...processingStats,
      operationsCount: prev.operationsCount + 1
    }));
  }, [inputText, mode, escapePHP, unescapePHP]);

  useEffect(() => {
    processText();
  }, [processText]);

  const showNotification = (message: string): void => {
    setNotification(message);
    setTimeout(() => setNotification(''), 3000);
  };

  const copyToClipboard = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(outputText);
      showNotification('Copied to clipboard!');
    } catch (err) {
      showNotification('Failed to copy');
    }
  };

  const downloadFile = (): void => {
    const blob = new Blob([outputText], { type: 'application/x-php' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${mode}d-php.php`;
    a.click();
    URL.revokeObjectURL(url);
    showNotification('File downloaded!');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          setInputText(result);
          showNotification('File loaded successfully!');
        }
      };
      reader.readAsText(file);
    }
  };

  const clearAll = (): void => {
    setInputText('');
    setOutputText('');
    setStats({
      charactersProcessed: 0,
      entitiesFound: 0,
      processingTime: 0,
      operationsCount: 0
    });
    showNotification('Cleared all content!');
  };

  const toggleFullscreen = (): void => {
    setIsFullscreen(!isFullscreen);
  };

  const loadSamplePHP = (): void => {
    setInputText(`<?php
// Sample PHP code with various string types
function processUserData($name, $email) {
    $message = "Hello, $name! Welcome to our site.";
    $sql = "SELECT * FROM users WHERE email = '$email'";
    
    // Single quoted string
    $path = 'C:\\Users\\Documents\\file.txt';
    
    // Double quoted string with special characters
    $multiline = "Line 1\nLine 2\tTabbed\r\nWindows line ending";
    
    // HTML content
    $html = '<div class="container">
        <h1>Welcome!</h1>
        <p>Your email: ' . $email . '</p>
    </div>';
    
    // JSON data
    $json = '{"name": "John Doe", "age": 30, "active": true}';
    
    // URL parameters
    $url = 'https://example.com/search?q=hello world&category=all';
    
    // Special characters test
    $special = "Quotes: \"double\" and 'single'\nBackslash: \\\nNull: \0\nDollar: $100";
    
    return [
        'message' => $message,
        'sql' => $sql,
        'path' => $path,
        'multiline' => $multiline,
        'html' => $html,
        'json' => $json,
        'url' => $url,
        'special' => $special
    ];
}
?>`);
  };

  const loadSampleEscaped = (): void => {
    setInputText(`&lt;?php
// Sample escaped PHP code
function processUserData(\\$name, \\$email) {
    \\$message = &quot;Hello, \\$name! Welcome to our site.&quot;;
    \\$sql = &quot;SELECT * FROM users WHERE email = '\\$email'&quot;;
    
    // Single quoted string
    \\$path = 'C:\\\\Users\\\\Documents\\\\file.txt';
    
    // Double quoted string with special characters
    \\$multiline = &quot;Line 1\\nLine 2\\tTabbed\\r\\nWindows line ending&quot;;
    
    // HTML content
    \\$html = '&lt;div class=&quot;container&quot;&gt;
        &lt;h1&gt;Welcome!&lt;/h1&gt;
        &lt;p&gt;Your email: ' . \\$email . '&lt;/p&gt;
    &lt;/div&gt;';
    
    // JSON data
    \\$json = '{&quot;name&quot;: &quot;John Doe&quot;, &quot;age&quot;: 30, &quot;active&quot;: true}';
    
    // URL parameters
    \\$url = 'https%3A//example.com/search%3Fq%3Dhello%20world%26category%3Dall';
    
    return [
        'message' =&gt; \\$message,
        'sql' =&gt; \\$sql,
        'path' =&gt; \\$path,
        'multiline' =&gt; \\$multiline,
        'html' =&gt; \\$html,
        'json' =&gt; \\$json,
        'url' =&gt; \\$url
    ];
}
?&gt;`);
  };

  const validatePHP = (): void => {
    if (!outputText.trim()) {
      showNotification('No PHP code to validate!');
      return;
    }
    
    // Basic PHP syntax validation
    const hasOpenTag = outputText.includes('<?php') || outputText.includes('<?');
    const hasValidSyntax = !outputText.includes('Parse error') && !outputText.includes('Fatal error');
    
    if (hasOpenTag && hasValidSyntax) {
      showNotification('✅ Basic PHP syntax looks valid!');
    } else {
      showNotification('⚠️ Potential PHP syntax issues detected');
    }
  };

  const containerClass = isFullscreen 
    ? "fixed inset-0 z-50 bg-gray-900" 
    : "min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-red-900";

  return (
    <div className={containerClass}>
      <div className="container mx-auto p-4 h-full flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-red-600 rounded-lg">
              <Code className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">PHP Escape/Unescape Tool</h1>
              <p className="text-gray-300 text-sm">Advanced PHP string processor with multiple escape methods</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <BarChart3 className="w-4 h-4" />
              <span>Analytics</span>
            </button>
            
            <button
              onClick={toggleFullscreen}
              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
            >
              {isFullscreen ? <Minimize2 className="w-5 h-5" /> : <Maximize2 className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mode Toggle */}
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gray-800 p-1 rounded-lg flex">
            <button
              onClick={() => setMode('escape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'escape' 
                  ? 'bg-purple-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <Zap className="w-4 h-4" />
              <span>Escape PHP</span>
            </button>
            <button
              onClick={() => setMode('unescape')}
              className={`px-6 py-2 rounded-md transition-all flex items-center space-x-2 ${
                mode === 'unescape' 
                  ? 'bg-red-600 text-white shadow-lg' 
                  : 'text-gray-300 hover:text-white'
              }`}
            >
              <RotateCcw className="w-4 h-4" />
              <span>Unescape PHP</span>
            </button>
          </div>
        </div>

        {/* Quote Style Selection */}
        {mode === 'escape' && (
          <div className="flex items-center justify-center mb-6">
            <div className="bg-gray-800 p-1 rounded-lg flex flex-wrap gap-1">
              {[
                { value: 'single', label: 'Single Quotes' },
                { value: 'double', label: 'Double Quotes' },
                { value: 'addslashes', label: 'addslashes()' },
                { value: 'htmlspecialchars', label: 'htmlspecialchars()' },
                { value: 'json', label: 'json_encode()' },
                { value: 'url', label: 'urlencode()' }
              ].map((style) => (
                <button
                  key={style.value}
                  onClick={() => setQuoteStyle(style.value as PhpQuoteStyle)}
                  className={`px-3 py-1 text-sm rounded transition-all ${
                    quoteStyle === style.value
                      ? 'bg-red-600 text-white'
                      : 'text-gray-300 hover:text-white hover:bg-gray-700'
                  }`}
                >
                  {style.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Analytics Panel */}
        {showAnalytics && (
          <div className="mb-6 bg-gray-800 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2" />
              Processing Analytics
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-purple-400">{stats.charactersProcessed.toLocaleString()}</div>
                <div className="text-gray-300 text-sm">Characters</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-red-400">{stats.entitiesFound}</div>
                <div className="text-gray-300 text-sm">Escapes</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-yellow-400">{stats.processingTime.toFixed(2)}ms</div>
                <div className="text-gray-300 text-sm">Process Time</div>
              </div>
              <div className="bg-gray-700 p-3 rounded-lg">
                <div className="text-2xl font-bold text-green-400">{stats.operationsCount}</div>
                <div className="text-gray-300 text-sm">Operations</div>
              </div>
            </div>
            
            {/* PHP Info */}
            <div className="mt-4 p-3 bg-gray-700 rounded-lg">
              <h4 className="text-white font-medium mb-2">Supported PHP Escape Methods</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                <div className="text-gray-300">
                  <strong>Single Quotes:</strong> {`\\' \\\\`}
                </div>
                <div className="text-gray-300">
                  <strong>Double Quotes:</strong> {`\\" \\\\ \\n \\r \\t \\$`}
                </div>
                <div className="text-gray-300">
                  <strong>addslashes():</strong> {`\\' \\" \\\\ \\0`}
                </div>
                <div className="text-gray-300">
                  <strong>htmlspecialchars():</strong> {`&amp; &quot; &#039; &lt; &gt;`}
                </div>
                <div className="text-gray-300">
                  <strong>json_encode():</strong> JSON escape sequences
                </div>
                <div className="text-gray-300">
                  <strong>urlencode():</strong> %XX URL encoding
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[600px]">
          {/* Input Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Input</h2>
              <div className="flex space-x-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".php,.txt,.json,.html,.js"
                    onChange={handleFileUpload}
                    className="hidden"
                  />
                  <div className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors">
                    <Upload className="w-4 h-4" />
                  </div>
                </label>
                <button
                  onClick={clearAll}
                  className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              placeholder={mode === 'escape' ? 'Enter PHP code or text to escape...' : 'Enter escaped PHP to unescape...'}
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {inputText.length.toLocaleString()}</span>
              <span>Lines: {inputText.split('\n').length}</span>
            </div>
          </div>

          {/* Output Section */}
          <div className="bg-gray-800 rounded-lg p-4 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Output</h2>
              <div className="flex space-x-2">
                <button
                  onClick={validatePHP}
                  disabled={!outputText}
                  className="p-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Code className="w-4 h-4" />
                </button>
                <button
                  onClick={copyToClipboard}
                  disabled={!outputText}
                  className="p-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={downloadFile}
                  disabled={!outputText}
                  className="p-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </div>
            
            <textarea
              value={outputText}
              readOnly
              placeholder="Processed PHP output will appear here..."
              className="flex-1 min-h-[500px] bg-gray-900 text-white p-4 rounded-lg border border-gray-600 resize-none font-mono text-sm leading-relaxed"
            />
            
            <div className="mt-2 flex justify-between text-gray-400 text-sm">
              <span>Characters: {outputText.length.toLocaleString()}</span>
              <span>Lines: {outputText.split('\n').length}</span>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={loadSamplePHP}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample PHP
            </button>
            <button
              onClick={loadSampleEscaped}
              className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
            >
              Load Sample Escaped
            </button>
            <button
              onClick={() => setMode(mode === 'escape' ? 'unescape' : 'escape')}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
            >
              Switch Mode
            </button>
            <button
              onClick={() => setInputText(inputText.replace(/\s+/g, ' ').trim())}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
            >
              Normalize Whitespace
            </button>
            <button
              onClick={validatePHP}
              disabled={!outputText}
              className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm"
            >
              Validate PHP
            </button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div className="fixed top-4 right-4 bg-green-600 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50">
            <CheckCircle2 className="w-4 h-4" />
            <span>{notification}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhpEscapeUnescapeTool;